import * as NavigationMenu from '@radix-ui/react-navigation-menu';

import { SUPPORTED_CHAIN_IDS } from '@/constants/chain';

import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';

import { ArrowUpRightIcon } from '@/ui/Icons/ArrowUpRightIcon';

import { routes } from '../../../utils/routes';
import {
  BlogIcon,
  CompanyIcon,
  CrossChainIcon,
  DeveloperApisIcon,
  SwapTokenIcon,
  LimitOrderIcon,
  LiquiditySourcesIcon,
  MatchaAutoIcon,
} from '../utils';
import animations from './animations.module.css';

/**
 * Component used in the marketing/features page and non-marketing pages.
 * Determines whether the page should be opened in a new tab or not.
 * isInFeaturesPage props is used to handle different behaviors based on the page type.
 */
//
interface LearnMoreDropDownListProps {
  hideBlogButton?: boolean;
  hideCompanyButton?: boolean;
  isInFeaturesPage?: boolean;
}

export const LearnMoreDropDownList = ({
  hideBlogButton,
  hideCompanyButton,
  isInFeaturesPage = false,
}: LearnMoreDropDownListProps) => {
  const { item } = popover();
  const featureLinkTarget = isInFeaturesPage ? '_self' : '_blank';

  return (
    <ul className="bg-special-background-modal">
      <li>
        {/* It's using <a> instead of <Link target="_blank">,
            because the compiler (Vercel build) is complaining the following error:
              Property 'target' does not exist on type 'IntrinsicAttributes & LinkProps & { children?: ReactNode; }'.

            It seems some missing config in Matcha's end
            A new create-next-app setup with next/link does support target="_blank" rel="noopener noreferrer"
            Needs more investigation
          */}
        <NavigationMenu.Link asChild>
          <a
            target={featureLinkTarget}
            href={routes.SWAP_TOKENS}
            className={item()}
            style={{ height: '64px' }}
          >
            <span
              className="rounded-full w-7 h-7 flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #F99704 0%, #F90 71.49%)',
                boxShadow:
                  '0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.14) inset',
              }}
            >
              <SwapTokenIcon />
            </span>
            <div className="ml-3">
              <div
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: ['text-token-gray-1600', 'flex', 'items-center', 'h-4'],
                })}
              >
                Swap Tokens
                {!isInFeaturesPage ? (
                  <ArrowUpRightIcon
                    className={`text-token-[rgba(94, 103, 115, 1)] ${animations['arrow-up-right-icon']} ${animations['animate-arrow-hover']}`}
                  />
                ) : null}
              </div>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['text-token-gray-800', 'fit', 'flex'],
                })}
              >
                Zero fee trades on 9+ million tokens
              </div>
            </div>
          </a>
        </NavigationMenu.Link>
        <NavigationMenu.Link asChild>
          <a
            target={featureLinkTarget}
            href={routes.MATCHA_AUTO}
            className={item()}
            style={{ height: '64px' }}
          >
            <span
              className="rounded-full w-7 h-7 flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(180deg, #009658 -20.83%, rgba(0, 185, 109, 0.60) 112.5%)',
                boxShadow:
                  '0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.14) inset',
              }}
            >
              <MatchaAutoIcon />
            </span>
            <div className="ml-3">
              <div
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: ['text-token-gray-1600', 'flex', 'items-center', 'h-4'],
                })}
              >
                Gasless Swaps
                {!isInFeaturesPage ? (
                  <ArrowUpRightIcon
                    className={`text-token-[rgba(94, 103, 115, 1)] ${animations['arrow-up-right-icon']} ${animations['animate-arrow-hover']}`}
                  />
                ) : null}
              </div>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['text-token-gray-800', 'fit', 'flex'],
                })}
              >
                Go gasless with Matcha
              </div>
            </div>
          </a>
        </NavigationMenu.Link>
        <NavigationMenu.Link asChild>
          <a
            target={featureLinkTarget}
            href={routes.LIQUIDITY_SOURCES}
            className={item()}
            style={{ height: '64px' }}
          >
            <span
              className="rounded-full w-7 h-7 flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #309AFC 0%, #02A4FF 73.31%)',
                boxShadow:
                  '0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.14) inset',
              }}
            >
              <LiquiditySourcesIcon />
            </span>
            <div className="ml-3">
              <div
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: ['text-token-gray-1600', 'flex', 'items-center', 'h-4'],
                })}
              >
                Liquidity Sources
                {!isInFeaturesPage ? (
                  <ArrowUpRightIcon
                    className={`text-token-[rgba(94, 103, 115, 1)] ${animations['arrow-up-right-icon']} ${animations['animate-arrow-hover']}`}
                  />
                ) : null}
              </div>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['text-token-gray-800', 'fit', 'flex'],
                })}
              >
                130+ liquidity sources across {SUPPORTED_CHAIN_IDS.length} chains
              </div>
            </div>
          </a>
        </NavigationMenu.Link>
        <NavigationMenu.Link asChild>
          <a
            target={featureLinkTarget}
            href={routes.LIMIT_ORDERS}
            className={item()}
            style={{ height: '64px' }}
          >
            <span
              className="rounded-full w-7 h-7 flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #FF656D 0%, #F55E66 76.04%)',
                boxShadow:
                  '0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.14) inset',
              }}
            >
              <LimitOrderIcon />
            </span>
            <div className="ml-3">
              <div
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: ['text-token-gray-1600', 'flex', 'items-center', 'h-4'],
                })}
              >
                Limit Orders
                {!isInFeaturesPage ? (
                  <ArrowUpRightIcon
                    className={`text-token-[rgba(94, 103, 115, 1)] ${animations['arrow-up-right-icon']} ${animations['animate-arrow-hover']}`}
                  />
                ) : null}
              </div>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['text-token-gray-800', 'fit', 'flex'],
                })}
              >
                Set the price you want to pay
              </div>
            </div>
          </a>
        </NavigationMenu.Link>
        <NavigationMenu.Link asChild>
          <a
            target={featureLinkTarget}
            href={routes.CROSS_CHAIN_SWAPS}
            className={item()}
            style={{ height: '64px' }}
          >
            <span
              className="rounded-full w-7 h-7 flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #5932F3 0%, #4E5DE7 74.61%)',
                boxShadow:
                  '0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.14) inset',
              }}
            >
              <CrossChainIcon />
            </span>
            <div className="ml-3">
              <div
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: ['text-token-gray-1600', 'flex', 'items-center', 'h-4'],
                })}
              >
                Cross Chain
                {!isInFeaturesPage ? (
                  <ArrowUpRightIcon
                    className={`text-token-[rgba(94, 103, 115, 1)] ${animations['arrow-up-right-icon']} ${animations['animate-arrow-hover']}`}
                  />
                ) : null}
              </div>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['text-token-gray-800', 'fit', 'flex'],
                })}
              >
                Swap across chains, in one trade
              </div>
            </div>
          </a>
        </NavigationMenu.Link>
      </li>

      <hr className="border-0 border-t border-solid -mx-1.5 border-special-border-divider" />

      {!hideBlogButton && (
        <li>
          <NavigationMenu.Link asChild>
            <a className={item()} style={{ height: '64px' }} href={routes.BLOG} target="_blank">
              <span
                className="rounded-full w-7 h-7 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(180deg, #2CB051 0%, #59B56D 77.36%)',
                  boxShadow:
                    '0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.14) inset',
                }}
              >
                <BlogIcon />
              </span>
              <div className="ml-3">
                <div
                  className={fontStyles({
                    variant: 'LABEL_MD',
                    class: ['text-token-gray-1600', 'flex', 'items-center', 'h-4'],
                  })}
                >
                  Blog
                  <ArrowUpRightIcon
                    className={`text-token-[rgba(94, 103, 115, 1)] ${animations['arrow-up-right-icon']} ${animations['animate-arrow-hover']}`}
                  />
                </div>
                <div
                  className={fontStyles({
                    variant: 'BODY_MD',
                    class: ['text-token-gray-800', 'fit', 'flex'],
                  })}
                >
                  Stay up-to-date on the latest news
                </div>
              </div>
            </a>
          </NavigationMenu.Link>
        </li>
      )}

      {!hideCompanyButton && (
        <li>
          <NavigationMenu.Link asChild>
            <a
              href={routes.ABOUT_MATCHA}
              className={item()}
              style={{ height: '64px' }}
              target="_blank"
            >
              <span
                className="rounded-full w-7 h-7 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(180deg, #2CB051 0%, #59B56D 78%)',
                  boxShadow:
                    '0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.14) inset',
                }}
              >
                <CompanyIcon />
              </span>
              <div className="ml-3">
                <div
                  className={fontStyles({
                    variant: 'LABEL_MD',
                    class: ['text-token-gray-1600', 'flex', 'items-center', 'h-4'],
                  })}
                >
                  Company
                  {!isInFeaturesPage ? (
                    <ArrowUpRightIcon
                      className={`text-token-[rgba(94, 103, 115, 1)] ${animations['arrow-up-right-icon']} ${animations['animate-arrow-hover']}`}
                    />
                  ) : null}
                </div>
                <div
                  className={fontStyles({
                    variant: 'BODY_MD',
                    class: ['text-token-gray-800', 'fit', 'flex'],
                  })}
                >
                  Get to know Matcha
                </div>
              </div>
            </a>
          </NavigationMenu.Link>
        </li>
      )}

      {!hideCompanyButton && !hideBlogButton ? (
        <hr className="border-0 border-t border-solid -mx-1.5 border-special-border-divider" />
      ) : null}

      <li>
        <NavigationMenu.Link asChild>
          <a
            className={item()}
            style={{ height: '64px' }}
            href={`${routes.ZEROEX_HOME}docs/introduction/introduction-to-0x`}
            target="_blank"
          >
            <span
              className="rounded-full w-7 h-7 flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #3F3F46 0%, #26272B 100%)',
                boxShadow:
                  '0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px -1px 0px 0px rgba(0, 0, 0, 0.14) inset',
              }}
            >
              <DeveloperApisIcon />
            </span>
            <div className="ml-3">
              <div
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: ['text-token-gray-1600', 'flex', 'items-center', 'h-4'],
                })}
              >
                Developer APIs
                <ArrowUpRightIcon
                  className={`text-token-[rgba(94, 103, 115, 1)] ${animations['arrow-up-right-icon']} ${animations['animate-arrow-hover']}`}
                />
              </div>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['text-token-gray-800', 'fit', 'flex'],
                })}
              >
                Embed swaps into your app with 0x
              </div>
            </div>
          </a>
        </NavigationMenu.Link>
      </li>
    </ul>
  );
};
